import { useId, useMemo } from "react";

// Cognimeter — «Cognitive Function Scale», порт production-референса
// CogNimbus/cognimeter/cognitive_gauge_v8.html (см. COGNIMETER_SPEC.md).
// Заголовок расположен в SVG на той же оси, что и шкала (CX).
// Легенду и кнопку «Tap for full report» рисует PhysicianScanPage.
// Вход — внутренний confidence 0..100 из ответа модели (confidence_pct); disp()
// кладёт пороги решений модели на фиксированные зоны шкалы. Компонент —
// чистое view: предсказание здесь не меняется. Patient-facing labeling follows
// the Sept 2026 Class II / PTY-exemption presentation: no MCI/dementia labels.
//
// Sept 2026: для обсуждения с FDA-экспертом компонент умеет рисовать несколько
// вариантов шкалы (`scale`) и формулировок под числом (`wording`). Дефолты —
// прод-вид (reference + current). Позиция стрелки/число не зависят от варианта.

const CX = 400, CY = 400, R = 300, BAND = 74;
const RI = R - BAND;              // 226
const RC = (R + RI) / 2;          // 263
const SWEEP = 240, A0 = 90 + SWEEP / 2;
const WL = 60;                    // визуальная граница NC|CI
const GZ: [number, number] = [53, 68.5]; // исходная серая зона
const P1 = "#9327d4";
const GC = "#98a3b1";
export const TEAL = "#157F76";  // фирменный цвет отчёта (заголовок, Cognimeter, радар)

// Дефолтные пороги v1.5.0 (×100); при наличии — берутся из response.thresholds
const DEFAULT_T = { rule_out: 17, rule_in: 55.5, severe: 90 };

const RESULT_COLORS = {
  expected: "#159a47",
  borderline: "#6b7280",
  impairment: "#b42318",
};

// Варианты шкалы: Balanced v2 с reference-screening легендой (прод),
// три зоны NC|MCI|Dementia + gray, две зоны NC|CI + gray, чистый градиент без зон.
export type CognimeterScale = "reference" | "three-zone" | "two-zone" | "gradient";
// Варианты формулировок под числом.
export type CognimeterWording = "likelihood" | "zones" | "current";

export const COGNIMETER_SCALES: { value: CognimeterScale; label: string }[] = [
  { value: "reference", label: "Balanced v2 — reference" },
  { value: "three-zone", label: "Three reference zones + gray zone" },
  { value: "two-zone", label: "Two reference zones + gray zone" },
  { value: "gradient", label: "Gradient only" },
];
export const COGNIMETER_WORDINGS: { value: CognimeterWording; label: string }[] = [
  { value: "likelihood", label: "Likelihood (likely / highly likely)" },
  { value: "zones", label: "Zone definitions" },
  { value: "current", label: "Current" },
];

const MCI_DEM = 85;               // якорь подписей MCI/Dementia; разделителя между ними нет

const STOPS: [number, [number, number, number]][] = [
  [0, [30, 160, 72]], [48, [64, 174, 64]], [60, [176, 198, 38]],
  [76, [246, 208, 18]], [87, [250, 138, 12]], [93, [230, 60, 26]], [100, [200, 18, 26]],
];

const ang = (p: number) => ((A0 - (p / 100) * SWEEP) * Math.PI) / 180;
const pt = (p: number, r: number) => ({ x: CX + r * Math.cos(ang(p)), y: CY - r * Math.sin(ang(p)) });

export interface CognimeterThresholds {
  rule_out?: number;
  rule_in?: number;
  severe?: number;
}

export interface CognimeterInterpretation {
  score: number;
  primary: string;
  secondary?: string;
  category: keyof typeof RESULT_COLORS;
}

export function cognimeterInterpretation(position: number): CognimeterInterpretation {
  const score = Math.round(100 - Math.max(0, Math.min(100, position)));

  if (score >= 47) {
    return {
      score,
      primary: "Performance consistent with normal cognition",
      category: "expected",
    };
  }

  if (score >= 33) {
    return {
      score,
      primary: "Borderline result",
      secondary: score >= 40
        ? "Position leans toward normal-cognition range"
        : "Position leans toward cognitive-impairment range",
      category: "borderline",
    };
  }

  return {
    score,
    primary: "Performance consistent with cognitive impairment",
    category: "impairment",
  };
}

export type CognimeterWordingResult = Omit<CognimeterInterpretation, "score">;

// Вариант «Likelihood» (август 2026): likely / highly likely по зонам дисплея.
export function cognimeterLikelihoodLabel(position: number): CognimeterWordingResult {
  if (position < GZ[0]) return { primary: "Normal cognition highly likely", secondary: undefined, category: "expected" };
  if (position < WL) return { primary: "Normal cognition likely", secondary: undefined, category: "borderline" };
  if (position < GZ[1]) return { primary: "Cognitive impairment likely", secondary: undefined, category: "borderline" };
  return { primary: "Cognitive impairment highly likely", secondary: undefined, category: "impairment" };
}

// Вариант «Zone definitions» (Andrey K., сент. 2026): результат по зоне +
// рекомендация; в серой зоне — куда склоняется позиция.
export function cognimeterZoneDefinition(position: number): CognimeterWordingResult {
  const score = Math.round(100 - Math.max(0, Math.min(100, position)));
  if (score >= 47) {
    return {
      primary: "Normal cognition result",
      category: "expected",
    };
  }
  if (score >= 33) {
    return {
      primary: "Borderline result",
      secondary: score >= 40
        ? "Position leans toward normal-cognition range"
        : "Position leans toward cognitive-impairment range",
      category: "borderline",
    };
  }
  return {
    primary: "Cognitive impairment result",
    category: "impairment",
  };
}

export function cognimeterReadout(position: number, wording: CognimeterWording): CognimeterInterpretation {
  const base = cognimeterInterpretation(position);
  if (wording === "likelihood") return { score: base.score, ...cognimeterLikelihoodLabel(position) };
  if (wording === "zones") return { score: base.score, ...cognimeterZoneDefinition(position) };
  return base;
}

// Разбивка primary на две строки для readout в SVG (по " — " или по середине).
function splitPrimary(primary: string): string[] {
  if (primary.includes(" — ")) {
    const [a, b] = primary.split(" — ");
    return [a + " —", b];
  }
  const words = primary.split(" ");
  if (words.length <= 3) return [primary];
  let best = 1, bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const l = words.slice(0, i).join(" ").length, r = words.slice(i).join(" ").length;
    if (Math.abs(l - r) < bestDiff) { bestDiff = Math.abs(l - r); best = i; }
  }
  return [words.slice(0, best).join(" "), words.slice(best).join(" ")];
}

export function cognimeterDisplayPosition(
  confidence: number,
  thresholds?: CognimeterThresholds | null
): number {
  const tOut = (thresholds?.rule_out ?? DEFAULT_T.rule_out / 100) * 100;
  const tIn = (thresholds?.rule_in ?? DEFAULT_T.rule_in / 100) * 100;
  const tSev = (thresholds?.severe ?? DEFAULT_T.severe / 100) * 100;
  const anchors: [number, number][] = [[0, 0], [tOut, 53], [tIn, 68.5], [tSev, 85], [100, 100]];
  const clamped = Math.max(0, Math.min(100, confidence));

  for (let i = 0; i < anchors.length - 1; i++) {
    const [x0, y0] = anchors[i];
    const [x1, y1] = anchors[i + 1];
    if (clamped <= x1) return y0 + ((y1 - y0) * (clamped - x0)) / (x1 - x0);
  }

  return 100;
}

function grad(p: number): string {
  for (let i = 0; i < STOPS.length - 1; i++) {
    const [p0, c0] = STOPS[i];
    const [p1, c1] = STOPS[i + 1];
    if (p <= p1) {
      const t = (p - p0) / (p1 - p0);
      const rgb = c0.map((c, j) => Math.round(c + (c1[j] - c) * t));
      return `rgb(${rgb.join(",")})`;
    }
  }
  return `rgb(${STOPS[STOPS.length - 1][1].join(",")})`;
}

function arcPathP(pa: number, pb: number, r: number): string {
  let d = "";
  const n = 44;
  for (let i = 0; i <= n; i++) {
    const p = pa + ((pb - pa) * i) / n;
    const q = pt(p, r);
    d += (i ? "L" : "M") + q.x.toFixed(1) + "," + q.y.toFixed(1) + " ";
  }
  return d;
}

export interface CognimeterProps {
  confidence: number; // internal model confidence 0..100 — response.confidence_pct
  thresholds?: CognimeterThresholds | null; // 0..1
  scale?: CognimeterScale;     // default: reference (Balanced v2)
  wording?: CognimeterWording; // default: current (прод)
}

export default function Cognimeter({
  confidence, thresholds, scale = "reference", wording = "current",
}: CognimeterProps) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const id = (name: string) => `cg-${uid}-${name}`;

  const staticParts = useMemo(() => {
    // цветная дуга из 180 сегментов
    const bandPolygons: { points: string; fill: string }[] = [];
    const N = 180;
    for (let i = 0; i < N; i++) {
      const p0 = (100 * i) / N, p1 = (100 * (i + 1)) / N;
      const o0 = pt(p0, R), o1 = pt(p1, R), i1 = pt(p1, RI), i0 = pt(p0, RI);
      bandPolygons.push({
        points: `${o0.x},${o0.y} ${o1.x},${o1.y} ${i1.x},${i1.y} ${i0.x},${i0.y}`,
        fill: grad((p0 + p1) / 2),
      });
    }

    const ticks: { x1: number; y1: number; x2: number; y2: number; major: boolean; label?: { x: number; y: number; text: number } }[] = [];
    for (let v = 0; v <= 100; v += 5) {
      const major = v % 10 === 0;
      const a = pt(v, R + 2), b = pt(v, R + (major ? 14 : 8));
      const tick: (typeof ticks)[number] = { x1: a.x, y1: a.y, x2: b.x, y2: b.y, major };
      if (major) {
        const tp = pt(v, R + 30);
        tick.label = { x: tp.x, y: tp.y + 5, text: 100 - v };
      }
      ticks.push(tick);
    }

    // исходная узкая серая зона: заливка + рамка снизу
    const RB = RI - 14, n = 44;
    let fd = "M" + pt(GZ[0], RI).x.toFixed(1) + "," + pt(GZ[0], RI).y.toFixed(1);
    for (let i = 1; i <= n; i++) {
      const q = pt(GZ[0] + ((GZ[1] - GZ[0]) * i) / n, RI);
      fd += " L" + q.x.toFixed(1) + "," + q.y.toFixed(1);
    }
    for (let i = 0; i <= n; i++) {
      const q = pt(GZ[1] - ((GZ[1] - GZ[0]) * i) / n, RB);
      fd += " L" + q.x.toFixed(1) + "," + q.y.toFixed(1);
    }
    fd += " Z";

    let bd = "M" + pt(GZ[0], RI).x.toFixed(1) + "," + pt(GZ[0], RI).y.toFixed(1)
      + " L" + pt(GZ[0], RB).x.toFixed(1) + "," + pt(GZ[0], RB).y.toFixed(1);
    for (let i = 0; i <= n; i++) {
      const p = GZ[0] + ((GZ[1] - GZ[0]) * i) / n;
      const q = pt(p, RB);
      bd += " L" + q.x.toFixed(1) + "," + q.y.toFixed(1);
    }
    bd += " L" + pt(GZ[1], RI).x.toFixed(1) + "," + pt(GZ[1], RI).y.toFixed(1);

    // исходный градиент серой зоны с пиком на разделителе NC|CI
    const RM = RI - 7;
    const e0 = pt(GZ[0], RM), e1 = pt(GZ[1], RM), ec = pt(WL, RM);
    const dx = e1.x - e0.x, dy = e1.y - e0.y;
    const off = ((ec.x - e0.x) * dx + (ec.y - e0.y) * dy) / (dx * dx + dy * dy);

    const sep = { a: pt(WL, R), b: pt(WL, RI) };

    return { bandPolygons, ticks, fd, bd, gg: { e0, e1, off }, sep };
  }, []);

  const p = cognimeterDisplayPosition(confidence, thresholds);
  const result = cognimeterReadout(p, wording);
  const col = RESULT_COLORS[result.category];
  const tip = pt(p, RI + 22);
  const lines = splitPrimary(result.primary);
  const showZones = scale !== "gradient";
  const showGray = scale !== "gradient";

  return (
    <svg
      viewBox="0 0 800 586"
      role="img"
      aria-label={`Cognitive function score ${result.score}. ${result.primary}`}
      style={{ display: "block", width: "100%", height: "auto" }}
    >
      <text x={CX} y={36} textAnchor="middle" fontSize={42} fontWeight={700} fill={TEAL}>
        Cognitive Function Scale
      </text>
      <defs>
        <marker id={id("nh")} viewBox="0 0 12 12" refX={8.5} refY={6} markerWidth={6} markerHeight={6} orient="auto">
          <path d="M1,1 L10.5,6 L1,11 Z" fill={P1} />
        </marker>
        <path id={id("zNC")} d={arcPathP(30 - 18, 30 + 18, RC - 9)} fill="none" />
        <path id={id("zCI")} d={arcPathP(60.5, 95.5, RC - 9)} fill="none" />
        <path id={id("zMCI")} d={arcPathP(WL + 1, MCI_DEM - 1, RC - 9)} fill="none" />
        <path id={id("zDem")} d={arcPathP(MCI_DEM + 1, 99.5, RC - 9)} fill="none" />
        <path id={id("zgray")} d={arcPathP(GZ[0] + 1, GZ[1] - 1, RI - 32)} fill="none" />
        <linearGradient
          id={id("gg")}
          gradientUnits="userSpaceOnUse"
          x1={staticParts.gg.e0.x.toFixed(1)} y1={staticParts.gg.e0.y.toFixed(1)}
          x2={staticParts.gg.e1.x.toFixed(1)} y2={staticParts.gg.e1.y.toFixed(1)}
        >
          <stop offset="0" stopColor={GC} stopOpacity="0.32" />
          <stop offset={staticParts.gg.off.toFixed(3)} stopColor={GC} stopOpacity="1" />
          <stop offset="1" stopColor={GC} stopOpacity="0.32" />
        </linearGradient>
      </defs>

      {/* статика: дуга, разделитель, тики, лейблы зон, серая зона */}
      <g>
        {staticParts.bandPolygons.map((poly, i) => (
          <polygon key={i} points={poly.points} fill={poly.fill} stroke={poly.fill} strokeWidth={0.7} />
        ))}
        {showZones && (
          <line
            x1={staticParts.sep.a.x} y1={staticParts.sep.a.y}
            x2={staticParts.sep.b.x} y2={staticParts.sep.b.y}
            stroke="#ffffff" strokeWidth={3} opacity={0.9}
          />
        )}
        {staticParts.ticks.map((t, i) => (
          <g key={i}>
            <line x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2} stroke="#9aa6b6" strokeWidth={t.major ? 2 : 1} />
            {t.label && (
              <text x={t.label.x} y={t.label.y} textAnchor="middle" fontSize={15} fontWeight={600} fill="#56616f">
                {t.label.text}
              </text>
            )}
          </g>
        ))}
        {showZones && (
          <text fontSize={28} fontWeight={700} fill="#ffffff" letterSpacing=".4">
            <textPath href={`#${id("zNC")}`} startOffset="50%" textAnchor="middle">Normal cognition</textPath>
          </text>
        )}
        {(scale === "two-zone" || scale === "reference") && (
          <text fontSize={24} fontWeight={700} fill="#ffffff" letterSpacing=".2">
            <textPath href={`#${id("zCI")}`} startOffset="50%" textAnchor="middle">Cognitive impairment</textPath>
          </text>
        )}
        {scale === "three-zone" && (
          <>
            <text fontSize={26} fontWeight={700} fill="#3a2c00" letterSpacing=".4">
              <textPath href={`#${id("zMCI")}`} startOffset="50%" textAnchor="middle">Mild impairment</textPath>
            </text>
            <text fontSize={20} fontWeight={700} fill="#ffffff" letterSpacing=".2">
              <textPath href={`#${id("zDem")}`} startOffset="50%" textAnchor="middle">Severe impairment</textPath>
            </text>
          </>
        )}
        {showGray && (
          <>
            <path d={staticParts.fd} fill={`url(#${id("gg")})`} stroke="none" />
            <path d={staticParts.bd} fill="none" stroke="#808d9c" strokeWidth={2.5} strokeLinecap="butt" strokeLinejoin="round" />
            <text fontSize={14} fontWeight={600} fill="#6b7a90" letterSpacing=".3">
              <textPath href={`#${id("zgray")}`} startOffset="50%" textAnchor="middle">gray zone</textPath>
            </text>
          </>
        )}
      </g>

      {/* динамика: стрелка + readout */}
      <g>
        <line
          x1={CX} y1={CY} x2={tip.x} y2={tip.y}
          stroke={P1} strokeWidth={7} strokeLinecap="round" markerEnd={`url(#${id("nh")})`}
        />
        <circle cx={CX} cy={CY} r={15} fill="#2c3645" />
        <circle cx={CX} cy={CY} r={6} fill="#fff" />
        <text x={CX} y={450} textAnchor="middle" fontSize={13} fontWeight={600} fill={TEAL}>
          Cognimeter
        </text>
        <text
          x={CX} y={498} textAnchor="middle" fontSize={54} fontWeight={700} fill={col}
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {result.score}
        </text>
        <text textAnchor="middle" fontSize={16} fontWeight={700} fill={col}>
          {lines.map((ln, i) => (
            <tspan key={i} x={CX} y={521 + i * 20}>{ln}</tspan>
          ))}
        </text>
        {result.secondary && (
          <text
            x={CX}
            y={lines.length === 1 ? 547 : 565}
            textAnchor="middle"
            fontSize={16}
            fontWeight={600}
            fill="#536274"
          >
            {result.secondary}
          </text>
        )}
      </g>
    </svg>
  );
}
